import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  AtaModelType,
  AtaModelsResponse,
  CreateAtaModelDto,
  AtaModelsMaxLinesResponse,
} from '../../types/telcobill-package-settings/telcobillPackageSettings.types';
import { ModelType, BrandWithModelsType } from '../../types/inventory/inventory.types';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';

export const getAtaModels = createAsyncThunk<AtaModelsResponse, number, { state: RootReducerState }>(
  'telcobillPackageSettings/getAtaModels',
  async (organizationId, thunkAPI) => {
    try {
      const response = await axios.get<AtaModelsResponse>(
        `${process.env.REACT_APP_API_URL}/telcobill/ata-models/${organizationId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error fetching ATA models!' });
    }
  }
);

export const createAtaModel = createAsyncThunk<AtaModelType, CreateAtaModelDto, { state: RootReducerState }>(
  'telcobillPackageSettings/createAtaModel',
  async (data, thunkAPI) => {
    try {
      const response = await axios.post<AtaModelType>(`${process.env.REACT_APP_API_URL}/telcobill/ata-models`, data);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error creating ATA model!' });
    }
  }
);

export const deleteAtaModel = createAsyncThunk<{ message: string }, number, { state: RootReducerState }>(
  'telcobillPackageSettings/deleteAtaModel',
  async (id, thunkAPI) => {
    try {
      const response = await axios.delete<{ message: string }>(
        `${process.env.REACT_APP_API_URL}/telcobill/ata-models/${id}`
      );
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error deleting ATA model!' });
    }
  }
);

export const getAtaModelsMaxLines = createAsyncThunk<AtaModelsMaxLinesResponse, number, { state: RootReducerState }>(
  'telcobillPackageSettings/getAtaModelsMaxLines',
  async (organizationId, thunkAPI) => {
    try {
      const response = await axios.get<AtaModelsMaxLinesResponse>(
        `${process.env.REACT_APP_API_URL}/telcobill/ata-models/orgs/${organizationId}/max-lines`
      );
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error fetching max lines!' });
    }
  }
);

export const getAvailableAtaBrands = createAsyncThunk<BrandWithModelsType[], void, { state: RootReducerState }>(
  'telcobillPackageSettings/getAvailableAtaBrands',
  async (_, thunkAPI) => {
    try {
      const response = await axios.get<BrandWithModelsType[]>(`${process.env.REACT_APP_API_URL}/device/brands`);

      const brandsWithAtaModels = response.data.filter(
        brand => brand.models && brand.models.some(model => model.isAta === true)
      );

      return brandsWithAtaModels;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error fetching available ATA brands!' });
    }
  }
);

export const getAvailableAtaModels = createAsyncThunk<ModelType[], void, { state: RootReducerState }>(
  'telcobillPackageSettings/getAvailableAtaModels',
  async (_, thunkAPI) => {
    try {
      const response = await axios.get<{ items: ModelType[]; meta: any }>(
        `${process.env.REACT_APP_API_URL}/device/models?isAta=true`
      );

      const models = Array.isArray(response.data) ? response.data : response.data.items || [];

      return models;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error fetching available ATA models!' });
    }
  }
);
